import { User, Company, Group, PBIReport } from '@esg/esg-global-types';
import { DashboardStatus } from '../context/DashboardContext';
import { useContext, useEffect, useState } from 'react';
import { getPBIReports, getReportId, validateReport } from '../lib/app/powerbi/pbi_report';
import { getPbiReportToken } from '../lib/app/powerbi/token';
import { FeedbackSnackbarContext } from '../context/FeedbackSnackbarContext';
import { getModuleId } from '../lib/app/navigation';
import { getViewAccess, ViewAccess } from '../util/user_access';
import { generateUuidV4 } from '../util/uuid';
import { log } from '../util/log';
import { MetadataError } from '@ep/error-handling';

const TOKEN_REFRESH_TIMEOUT_MS = 25 * 60 * 1000;

const usePbiTokenManager = (
  user: User | null,
  company: Company | null,
  group: Group | null
): {
  validPbiReports: Array<PBIReport>;
  invalidPbiReports: Array<PBIReport | null>;
  dashboardStatus: DashboardStatus;
  lastTokenRefresh: number;
} => {
  // get all reports
  const [dashboardStatus, setDashboardStatus] = useState<DashboardStatus>('idle');
  const [validPbiReports, setValidPbiReports] = useState<Array<PBIReport>>([]);
  const [invalidPbiReports, setInvalidPbiReports] = useState<Array<PBIReport | null>>([]);
  const [lastTokenRefresh, setLastTokenRefresh] = useState<number>(0);
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);

  useEffect(() => {
    let stopped = false;
    (async () => {
      // Reset states to avoid stale data being exposed.

      // if any of the following or null, return default values.
      if (!user || !company || !group) return;
      try {
        setDashboardStatus('prefetching');
        // Get all reports.
        const company_reports: Array<PBIReport | null> = await getPBIReports(group.id, company.id);
        if (stopped) return;
        const valid_reports: Array<PBIReport> = [];
        const invalid_reports: Array<PBIReport | null> = [];
        // Separate reports into valid and invalid report arrays.
        company_reports.forEach((report) => {
          validateReport(report) ? valid_reports.push(report) : invalid_reports.push(report);
        });
        setValidPbiReports(valid_reports);
        setInvalidPbiReports(invalid_reports);
        setDashboardStatus('bootstrapping');
        // Get report tokens and store them in the cache so long.
        for (const report of valid_reports) {
          try {
            const report_id: string = getReportId(report.embed_url);
            const module_id: string = getModuleId(report.view);
            const access: ViewAccess = getViewAccess(
              user,
              group.id,
              company.id,
              `${module_id}.${report.view}`
            );
            access !== 'none' && (await getPbiReportToken(report.dataset_id, report_id, false));
            if (stopped) return;
          } catch {
            setInvalidPbiReports((prev) => [...prev, report]);
          }
        }
        if (stopped) return;
        setDashboardStatus('preloaded');
      } catch (err: unknown) {
        const tracking_id: string = generateUuidV4();
        log(
          'error',
          new MetadataError(
            err instanceof Error
              ? err.message
              : `Error: usePbiTokenManager failed on an unknown error while initialising.`,
            null,
            tracking_id
          )
        );
        if (stopped) return;
        setFeedbackData({
          message: 'Failed to load dashboards. Please refresh.',
          state: true,
          type: 'error'
        });
        setDashboardStatus('error');
      }
    })();
    return () => {
      setDashboardStatus('idle');
      setValidPbiReports([]);
      setInvalidPbiReports([]);
      if (user && group && company) {
        localStorage.removeItem('pbi_tokens');
        localStorage.removeItem('pbi_iv_key');
      }
      stopped = true;
    };
  }, [user, group, company]);

  useEffect(() => {
    if (validPbiReports.length === 0) return;
    // Refresh the token cache.
    const interval_id = setInterval(async () => {
      // reset our token cache
      for (const report of validPbiReports) {
        try {
          const report_id: string = getReportId(report.embed_url);
          await getPbiReportToken(report.dataset_id, report_id, true);
        } catch (err: unknown) {
          const tracking_id: string = generateUuidV4();
          log(
            'error',
            new MetadataError(
              err instanceof Error
                ? err.message
                : `Error: Token refresh failed for report: ${report.view}`,
              null,
              tracking_id
            )
          );
          setFeedbackData({
            message: 'Token refresh failed. Please reload the page.',
            state: true,
            type: 'error'
          });
        }
      }
      // send the signal that a refresh has occurred.
      setLastTokenRefresh(Date.now());
    }, TOKEN_REFRESH_TIMEOUT_MS);
    return () => clearInterval(interval_id);
  }, [validPbiReports]);

  return {
    validPbiReports,
    invalidPbiReports,
    dashboardStatus,
    lastTokenRefresh
  };
};

export default usePbiTokenManager;
